import React, { useState } from 'react';
import { supabase } from '../utils/supabase';
import { useTranslation } from '../utils/translations';

interface LanguageSelectionProps {
  onSelect: (targetLang: string, nativeLang: string) => void;
}

export function LanguageSelection({ onSelect }: LanguageSelectionProps) {
  const [targetLanguage, setTargetLanguage] = useState(() => localStorage.getItem('targetLanguage') || 'Dutch');
  const [nativeLanguage, setNativeLanguage] = useState(() => localStorage.getItem('nativeLanguage') || 'English');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t, isRTL } = useTranslation(nativeLanguage);

  const handleLanguageChange = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Clear admin language to ensure user language takes precedence
      localStorage.removeItem('adminLanguage');

      // Update profile with new language preference
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Store in localStorage first to ensure immediate effect
      localStorage.setItem('targetLanguage', targetLanguage);
      localStorage.setItem('nativeLanguage', nativeLanguage);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          native_language: nativeLanguage,
          target_language: targetLanguage,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Call parent handler
      onSelect(targetLanguage, nativeLanguage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update language preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {t('Select your languages')}
          </h2>
          {error && (
            <div className="mt-2 text-center text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <div className="mt-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t('Target Language')}
              </label>
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="Dutch">Dutch</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                {t('Your Language')}
              </label>
              <select
                value={nativeLanguage}
                onChange={(e) => setNativeLanguage(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="English">English</option>
                <option value="Persian">فارسی (Persian)</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleLanguageChange}
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {loading ? t('Updating...') : t('Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}